import apmInstance from 'utilities/loggerv2/apmInit';
import { LogContext, Logger } from './logger';
import ErrorCodeDefinition from './errorCodeDefinition';
import LogLevel from './loglevel';

class ClientLogger implements Logger {
  private serviceName: string;

  private apmInstance;

  private userId?: string;

  private sessionId?: string;

  constructor(serviceName: string) {
    this.serviceName = serviceName;

    this.apmInstance = apmInstance;
  }

  setUserId(userId: string): void {
    this.userId = userId;
    this.apmInstance?.setUserContext({
      id: userId,
    });
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    if (this.apmInstance) {
      const currentContext = this.apmInstance.getCurrentTransaction();
      if (currentContext) {
        currentContext.addLabels({
          sessionId,
        });
      }
    }
  }

  private getBrowserInfo(): Record<string, any> {
    if (typeof window === 'undefined') return {};

    return {
      userAgent: window.navigator.userAgent,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      url: window.location.href,
      referrer: document.referrer || '',
    };
  }

  private enrichContext(context?: LogContext): LogContext {
    return {
      ...context,
      service: this.serviceName,
      env: process.env.NODE_ENV || 'development',
      timestamp: new Date().toISOString(),
      // Keep the full browser object for console logs
      browser: this.getBrowserInfo(),
      ...(this.userId && { userId: this.userId }),
      ...(this.sessionId && { sessionId: this.sessionId }),
    };
  }

  // Add this new method to prepare labels for APM
  private prepareLabelsForAPM(enrichedContext: LogContext, level: string, message: string): Record<string, any> {
    const browserInfo = this.getBrowserInfo();

    return {
      message,
      level,
      ...enrichedContext,
      // Replace browser object with flattened properties
      browser: JSON.stringify(browserInfo),
      'browser.userAgent': browserInfo.userAgent,
      'browser.viewportWidth': browserInfo.viewportWidth,
      'browser.viewportHeight': browserInfo.viewportHeight,
      'browser.url': browserInfo.url,
      'browser.referrer': browserInfo.referrer,
    };
  }

  debug(message: string, context?: LogContext): void {
    const enrichedContext = this.enrichContext(context);
    if (process.env.NODE_ENV !== 'production') {
      console.debug(`[DEBUG] ${message}`, enrichedContext);
    }

    if (this.apmInstance) {
      const currentTransaction = this.apmInstance.getCurrentTransaction();
      if (currentTransaction) {
        currentTransaction.addLabels(this.prepareLabelsForAPM(enrichedContext, 'debug', message));
      }
    }
  }

  info(message: string, context?: LogContext): void {
    const enrichedContext = this.enrichContext(context);
    if (process.env.NODE_ENV !== 'production') {
      console.info(`[INFO] ${message}`, enrichedContext);
    }

    if (this.apmInstance) {
      const currentTransaction = this.apmInstance.getCurrentTransaction();
      if (currentTransaction) {
        currentTransaction.addLabels(this.prepareLabelsForAPM(enrichedContext, 'info', message));
      }
    }
  }

  warn(message: string, context?: LogContext): void {
    const enrichedContext = this.enrichContext(context);
    console.warn(`[WARN] ${message}`, enrichedContext);

    if (this.apmInstance) {
      const currentTransaction = this.apmInstance.getCurrentTransaction();
      if (currentTransaction) {
        currentTransaction.addLabels(this.prepareLabelsForAPM(enrichedContext, 'warn', message));
      }
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    const enrichedContext = this.enrichContext(context);
    console.error(`[ERROR] ${message}`, error, enrichedContext);

    if (this.apmInstance) {
      // Set context first
      const currentTransaction = this.apmInstance.getCurrentTransaction();
      if (currentTransaction) {
        currentTransaction.addLabels(this.prepareLabelsForAPM(enrichedContext, 'error', message));
      }

      // Then capture the error
      this.apmInstance.captureError(error || message);
    }
  }

  fatal(message: string, error?: Error, context?: LogContext): void {
    const enrichedContext = { ...this.enrichContext(context), isFatal: true };
    console.error(`[FATAL] ${message}`, error, enrichedContext);

    if (this.apmInstance) {
      // Set context first
      const currentTransaction = this.apmInstance.getCurrentTransaction();
      if (currentTransaction) {
        currentTransaction.addLabels(this.prepareLabelsForAPM(enrichedContext, 'fatal', message));
      }

      // Then capture the error
      this.apmInstance.captureError(error || new Error(message));
    }
  }

  logWithCode(errorCode: ErrorCodeDefinition, error?: Error, context?: LogContext): void {
    if (!errorCode || typeof errorCode !== 'object') {
      console.error('Invalid error code provided to logWithCode', errorCode);
      this.error('Invalid error code provided to logger', error, context);
      return;
    }

    const fullMessage = `[${errorCode.code || 'UNKNOWN_CODE'}] ${errorCode.message || 'Unknown error'}`;
    const enrichedContext = {
      ...this.enrichContext(context),
      errorCode: errorCode.code || 'UNKNOWN_CODE',
      errorCategory: errorCode.category || 'UNKNOWN',
      errorDescription: errorCode.description || '',
    };

    switch (errorCode.level) {
      case LogLevel.DEBUG:
        this.debug(fullMessage, enrichedContext);
        break;
      case LogLevel.INFO:
        this.info(fullMessage, enrichedContext);
        break;
      case LogLevel.WARN:
        this.warn(fullMessage, enrichedContext);
        break;
      case LogLevel.ERROR:
        this.error(fullMessage, error, enrichedContext);
        break;
      case LogLevel.FATAL:
        this.fatal(fullMessage, error, enrichedContext);
        break;
      default:
        this.info(fullMessage, enrichedContext);
    }
  }
}

export default ClientLogger;
